import React from 'react';
import StatsCard from "./StatsCard";
import ActivityItem from "./ActivityItem";
import QuickAction from "./QuickAction";
import ApplicationsChart from "./ApplicationsChart";
import MatchingChart from "./MatchingChart";

const Dashboard = () => {
  const statsData = [
    {
      title: 'Total Candidates',
      value: '2,847',
      trend: '12% increase',
      trendType: 'up',
      icon: 'fas fa-user-graduate text-hunar-blue',
      iconBg: 'bg-blue-100',
      borderColor: 'border-hunar-blue'
    },
    {
      title: 'Active Employers',
      value: '324',
      trend: '5% increase',
      trendType: 'up',
      icon: 'fas fa-building text-hunar-purple',
      iconBg: 'bg-purple-100',
      borderColor: 'border-hunar-purple'
    },
    {
      title: 'Job Postings',
      value: '1,563',
      trend: '3% decrease',
      trendType: 'down',
      icon: 'fas fa-briefcase text-hunar-teal',
      iconBg: 'bg-teal-100',
      borderColor: 'border-hunar-teal'
    },
    {
      title: 'AI Match Rate',
      value: '87%',
      trend: '8% improvement',
      trendType: 'up',
      icon: 'fas fa-robot text-hunar-orange',
      iconBg: 'bg-yellow-100',
      borderColor: 'border-hunar-orange'
    }
  ];

  const activities = [
    {
      icon: 'fas fa-user-plus text-green-600',
      iconBg: 'bg-green-100',
      title: 'New candidate registered',
      description: 'Sarah Johnson • 2 hours ago',
      badge: 'Candidate',
      badgeColor: 'bg-green-100 text-green-800'
    },
    {
      icon: 'fas fa-briefcase text-blue-600',
      iconBg: 'bg-blue-100',
      title: 'New job posted by TechCorp',
      description: 'Senior Developer • 5 hours ago',
      badge: 'Job',
      badgeColor: 'bg-blue-100 text-blue-800'
    },
    {
      icon: 'fas fa-handshake text-purple-600',
      iconBg: 'bg-purple-100',
      title: 'Successful AI match',
      description: 'Mark Williams → Data Scientist • 1 day ago',
      badge: 'Match',
      badgeColor: 'bg-purple-100 text-purple-800'
    },
    {
      icon: 'fas fa-building text-yellow-600',
      iconBg: 'bg-yellow-100',
      title: 'New employer registered',
      description: 'Innovate Labs • 1 day ago',
      badge: 'Employer',
      badgeColor: 'bg-yellow-100 text-yellow-800'
    }
  ];

  const quickActions = [
    {
      icon: 'fas fa-user-plus text-hunar-blue',
      iconBg: 'bg-blue-100',
      label: 'Add Candidate',
      to: '/hpanel/candidates/add'
    },
    {
      icon: 'fas fa-building text-hunar-purple',
      iconBg: 'bg-purple-100',
      label: 'Add Employer',
      to: '/hpanel/employers/add'
    },
    {
      icon: 'fas fa-briefcase text-hunar-teal',
      iconBg: 'bg-teal-100',
      label: 'Post Job',
      to: '/hpanel/jobs/add'
    },
    {
      icon: 'fas fa-robot text-hunar-orange',
      iconBg: 'bg-orange-100',
      label: 'AI Analysis',
      to: '/hpanel/ai-analysis'
    }
  ];

  return (
    <div>
      {/* SEO Settings */}
      <title>{`Admin Dashboard | ${process.env.REACT_APP_NAME}`}</title>
      <meta name="robots" content="noindex, nofollow" />

      {/* Dashboard Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white">Dashboard</h2>
        <p className="text-gray-400">Welcome back, Admin! Here's what's happening with your platform today.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statsData.map((stat, index) => (
          <StatsCard key={index} {...stat} />
        ))}
      </div>

      {/* Charts and Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Application Trends */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-800">Application Trends</h3>
            <select className="text-sm border border-gray-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option>Last 7 days</option>
              <option>Last 30 days</option>
              <option selected>Last 90 days</option>
            </select>
          </div>
          <div className="h-64">
            <ApplicationsChart />
          </div>
        </div>

        {/* AI Matching Performance */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-800">AI Matching Performance</h3>
            <div className="flex space-x-2">
              <button className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded">Success Rate</button>
              <button className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Response Time</button>
            </div>
          </div>
          <div className="h-64">
            <MatchingChart />
          </div>
        </div>
      </div>

      {/* Recent Activity and Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm p-6 lg:col-span-2">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-800">Recent Activity</h3>
            <button className="text-primary-600 text-sm font-medium">View All</button>
          </div>
          <div className="space-y-4">
            {activities.map((activity, index) => (
              <ActivityItem key={index} {...activity} />
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-6">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-4">
            {quickActions.map((action, index) => (
              <QuickAction key={index} {...action} />
            ))}
          </div>
          
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-3">AI Insights</h4>
            <div className="bg-primary-50 p-4 rounded-lg">
              <p className="text-xs text-primary-700">
                <i className="fas fa-lightbulb text-primary-600 mr-1"></i>
                Your platform has a 87% match success rate. Consider optimizing job descriptions for better AI matching.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Performance */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-6">Platform Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-3">
              <i className="fas fa-bolt text-xl"></i>
            </div>
            <h4 className="font-medium text-gray-800">Response Time</h4>
            <p className="text-2xl font-bold text-gray-800 mt-1">0.8s</p>
            <p className="text-green-500 text-xs mt-1">
              <i className="fas fa-arrow-up mr-1"></i> 15% faster
            </p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-3">
              <i className="fas fa-server text-xl"></i>
            </div>
            <h4 className="font-medium text-gray-800">Uptime</h4>
            <p className="text-2xl font-bold text-gray-800 mt-1">99.9%</p>
            <p className="text-green-500 text-xs mt-1">
              <i className="fas fa-check mr-1"></i> All systems operational
            </p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 text-purple-600 mb-3">
              <i className="fas fa-users text-xl"></i>
            </div>
            <h4 className="font-medium text-gray-800">Active Users</h4>
            <p className="text-2xl font-bold text-gray-800 mt-1">3,171</p>
            <p className="text-green-500 text-xs mt-1">
              <i className="fas fa-arrow-up mr-1"></i> 234 this week
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;